import React from 'react';
import { Phone, MessageCircle, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { trackEvent } from './PageviewTracker';

const FinalCTASection = () => {
  const { settings } = useSettings();
  const contact = settings?.contact || {};
  const home = settings?.home || {};
  const cta = home.final_cta || {};

  const title = cta.title || 'Bereit für unvergessliche Momente?';
  const subtitle = cta.subtitle || 'Jetzt unverbindlich anfragen – wir antworten in der Regel innerhalb von 1 Stunde.';
  const eyebrow = cta.eyebrow || 'Termin anfragen';
  const btnInquiry = cta.button_inquiry_text || 'Jetzt Mietanfrage stellen';
  const btnPhone = cta.button_phone_text || 'Anrufen';
  const btnWa = cta.button_whatsapp_text || 'WhatsApp Chat';
  const footnote = cta.footnote || '✓ Antwort innerhalb 24 Stunden · ✓ Keine Anzahlung nötig · ✓ Rundum Service';

  const phone = contact.phone;
  const phoneHref = phone ? `tel:${phone.replace(/\s/g, '')}` : null;

  const waNumber = (contact.whatsapp_number || '').replace(/\D/g, '');
  const waText = encodeURIComponent(`Hallo ${settings?.site_name || 'Hüpfburgen Verleih'}, ich habe eine Frage zu Ihren Hüpfburgen.`);
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : null;

  return (
    <section
      className="py-12 md:py-20 relative"
      style={{ background: '#005898' }}
      data-testid="final-cta-section"
    >

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-block bg-yellow-400 text-sky-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
          {eyebrow}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
          {title}
        </h2>
        <p className="mt-4 text-sky-50/90 text-base md:text-lg max-w-2xl mx-auto">
          {subtitle}
        </p>

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-4 max-w-sm sm:max-w-none mx-auto">
          <Link
            to="/mietanfrage"
            onClick={() => trackEvent('cta_inquiry_click', { origin: 'final_cta' })}
            className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-sky-900 font-bold rounded-full text-sm sm:text-base px-5 py-2.5 sm:px-7 sm:py-4 shadow-xl active:scale-95 transition-all w-full sm:w-auto"
            data-testid="cta-mietanfrage"
          >
            <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5" />
            {btnInquiry}
          </Link>

          {phoneHref && (
            <a
              href={phoneHref}
              onClick={() => trackEvent('cta_phone_click', { origin: 'final_cta' })}
              className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold rounded-full text-sm sm:text-base px-5 py-2.5 sm:px-7 sm:py-4 border border-white/20 transition-all w-full sm:w-auto"
              data-testid="cta-phone"
            >
              <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
              {btnPhone}: {phone}
            </a>
          )}

          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_whatsapp_click', { origin: 'final_cta' })}
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-full text-sm sm:text-base px-5 py-2.5 sm:px-7 sm:py-4 shadow-xl active:scale-95 transition-all w-full sm:w-auto text-white"
              style={{ background: '#25D366' }}
              data-testid="cta-whatsapp"
            >
              <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
              {btnWa}
            </a>
          )}
        </div>

        <p className="mt-8 text-sky-100/80 text-sm">
          {footnote}
        </p>
      </div>
    </section>
  );
};

export default FinalCTASection;
